import { supabase } from "@/lib/supabase";
import type { UserRow, UserUpdate } from "@/types/domain";

export class AuthError extends Error {
  public override readonly cause?: unknown;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "AuthError";
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

async function getCurrentUserProfile(): Promise<UserRow | null> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    throw new AuthError("Failed to load session", { cause: sessionError });
  }
  const userId = sessionData.session?.user.id;
  if (userId === undefined) return null;

  const { data, error } = await supabase.from("users").select("*").eq("id", userId).maybeSingle();
  if (error) {
    throw new AuthError("Failed to load user profile", { cause: error });
  }
  return data;
}

export interface ProfileSaveOptions {
  expectedUpdatedAt?: string;
  logoBlob?: Blob | null;
  removeLogo?: boolean;
}

async function updateUserProfile(
  patch: UserUpdate,
  options?: ProfileSaveOptions,
): Promise<UserRow> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    throw new AuthError("Failed to load session", { cause: sessionError });
  }
  const userId = sessionData.session?.user.id;
  if (userId === undefined) {
    throw new AuthError("Not authenticated");
  }

  const finalPatch = { ...patch };
  if (options?.removeLogo) finalPatch.business_logo_url = null;
  if (options?.logoBlob) {
    const blob = options.logoBlob;
    if (
      blob.size > 5 * 1024 * 1024 ||
      !["image/jpeg", "image/png", "image/webp"].includes(blob.type)
    )
      throw new AuthError("Choose a JPG, PNG or WebP logo under 5 MB.");
    const path = `${userId}/${crypto.randomUUID()}.${blob.type === "image/png" ? "png" : blob.type === "image/webp" ? "webp" : "jpg"}`;
    const { error: uploadError } = await supabase.storage
      .from(BUSINESS_LOGOS_BUCKET)
      .upload(path, blob, {
        cacheControl: "3600",
        upsert: false,
        contentType: blob.type,
      });
    if (uploadError)
      throw new AuthError("Logo upload failed. Your changes are still here.", {
        cause: uploadError,
      });
    finalPatch.business_logo_url = supabase.storage
      .from(BUSINESS_LOGOS_BUCKET)
      .getPublicUrl(path).data.publicUrl;
  }
  // Preserve old bytes and the working copy even if the server committed but its response was lost.
  let query = supabase.from("users").update(finalPatch).eq("id", userId);
  if (options?.expectedUpdatedAt !== undefined)
    query = query.eq("updated_at", options.expectedUpdatedAt);
  const { data, error } = await query.select("*").maybeSingle();
  if (error) {
    throw new AuthError("Settings could not be saved. Your changes are still here.", {
      cause: error,
    });
  }
  if (!data)
    throw new AuthError(
      "Settings changed in another tab. Your changes are still here. Open Settings in a new tab to compare, or discard your edits to load the latest values.",
    );
  return data;
}

async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    throw new AuthError(error.message, { cause: error });
  }
}

async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw new AuthError(error.message, { cause: error });
  }
}

async function deleteAccount(): Promise<void> {
  const { error } = await supabase.rpc("delete_user_account");
  if (error) {
    throw new AuthError(error.message, { cause: error });
  }
}

const BUSINESS_LOGOS_BUCKET = "business-logos";

async function uploadBusinessLogo(userId: string, blob: Blob): Promise<string> {
  const path = `${userId}/logo.jpg`;
  const { error: uploadError } = await supabase.storage
    .from(BUSINESS_LOGOS_BUCKET)
    .upload(path, blob, {
      cacheControl: "3600",
      upsert: true,
      contentType: blob.type === "" ? "image/jpeg" : blob.type,
    });
  if (uploadError) {
    throw new AuthError("Failed to upload business logo", { cause: uploadError });
  }

  const { data: publicUrlData } = supabase.storage.from(BUSINESS_LOGOS_BUCKET).getPublicUrl(path);
  const cacheBustedUrl = `${publicUrlData.publicUrl}?t=${Date.now().toString()}`;

  const { error: updateError } = await supabase
    .from("users")
    .update({ business_logo_url: cacheBustedUrl })
    .eq("id", userId);
  if (updateError) {
    throw new AuthError("Failed to persist business logo URL", { cause: updateError });
  }

  return cacheBustedUrl;
}

async function removeBusinessLogo(userId: string): Promise<void> {
  const path = `${userId}/logo.jpg`;
  const { error: removeError } = await supabase.storage.from(BUSINESS_LOGOS_BUCKET).remove([path]);
  if (removeError) {
    throw new AuthError("Failed to remove business logo", { cause: removeError });
  }

  const { error: updateError } = await supabase
    .from("users")
    .update({ business_logo_url: null })
    .eq("id", userId);
  if (updateError) {
    throw new AuthError("Failed to clear business logo URL", { cause: updateError });
  }
}

export const authRepository = {
  getCurrentUserProfile,
  updateUserProfile,
  signIn,
  signOut,
  deleteAccount,
  uploadBusinessLogo,
  removeBusinessLogo,
} as const;
